import { useState, useEffect, useCallback, useMemo } from 'react'
import { Shield, ArrowLeft, Globe, CheckCircle2, Copy, ExternalLink, AlertTriangle, Info, Loader2, Wifi, WifiOff, RefreshCw, Trash2, ChevronDown, Activity, X, Wrench, TrendingUp } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { vpnStatus, vpnReconnect, vpnDisable, vpnRemove, vpnConnectKey, vpnInstallLog, vpnSetFlag } from '../api'

type PingResult = { alive: boolean; avg_ms: number | null; loss_pct: number }
type HttpResult = { ok: boolean; http_code: number | null; time_total_ms: number | null; ip_seen: string | null; error?: string | null }
type GeoInfo = {
  ip?: string | null
  country?: string | null
  country_code?: string | null
  region_name?: string | null
  city?: string | null
  zip?: string | null
  isp?: string | null
  org?: string | null
  as?: string | null
  reverse?: string | null
  timezone?: string | null
  mobile?: boolean
  proxy?: boolean
  hosting?: boolean
  lat?: number | null
  lon?: number | null
}

function flagEmoji(cc?: string | null): string {
  if (!cc || cc.length !== 2) return '🌐'
  const A = 0x1F1E6
  const up = cc.toUpperCase()
  return String.fromCodePoint(A + up.charCodeAt(0) - 65) + String.fromCodePoint(A + up.charCodeAt(1) - 65)
}

function GeoCard({ title, geo, highlight = false }: { title: string; geo?: GeoInfo | null; highlight?: boolean }) {
  if (!geo) {
    return (
      <div className="rounded-md border border-border bg-black/10 p-2.5 text-xs">
        <div className="font-semibold mb-1 text-muted-foreground">{title}</div>
        <div className="text-muted-foreground text-xs">не удалось получить данные</div>
      </div>
    )
  }
  const badgeCls = highlight
    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    : 'bg-muted/40 text-muted-foreground border-border'
  return (
    <div className={`rounded-md border ${highlight ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border bg-black/10'} p-2.5 text-xs space-y-1`}>
      <div className="flex items-center gap-2">
        <span className="text-base leading-none">{flagEmoji(geo.country_code)}</span>
        <span className="font-semibold">{title}</span>
        {geo.ip && (
          <code className={`ml-auto px-1.5 py-0.5 rounded text-xs ${badgeCls}`}>{geo.ip}</code>
        )}
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-muted-foreground">
        {geo.country && (
          <>
            <span>Страна:</span>
            <span className="text-foreground">
              {geo.country}{geo.region_name ? `, ${geo.region_name}` : ''}{geo.city ? `, ${geo.city}` : ''}
            </span>
          </>
        )}
        {geo.isp && (
          <>
            <span>ISP:</span>
            <span className="text-foreground truncate">{geo.isp}</span>
          </>
        )}
        {geo.as && (
          <>
            <span>AS:</span>
            <span className="text-foreground truncate font-mono text-[11px]">{geo.as}</span>
          </>
        )}
        {geo.reverse && (
          <>
            <span>PTR:</span>
            <span className="text-foreground truncate font-mono text-[11px]">{geo.reverse}</span>
          </>
        )}
        {geo.timezone && (
          <>
            <span>TZ:</span>
            <span className="text-foreground">{geo.timezone}</span>
          </>
        )}
      </div>
      {(geo.hosting || geo.proxy || geo.mobile) && (
        <div className="flex flex-wrap gap-1 pt-1">
          {geo.hosting && <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-amber-500/30 text-amber-400">hosting</Badge>}
          {geo.proxy && <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-red-500/30 text-red-400">proxy/vpn detected</Badge>}
          {geo.mobile && <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-blue-500/30 text-blue-400">mobile</Badge>}
        </div>
      )}
    </div>
  )
}

function PingRow({ icon, label, target, result }: {
  icon: 'ok' | 'fail' | 'info'
  label: string
  target?: string
  result?: PingResult
}) {
  const IconEl = icon === 'ok' ? CheckCircle2 : icon === 'fail' ? AlertTriangle : Info
  const iconCls = icon === 'ok' ? 'text-emerald-400' : icon === 'fail' ? 'text-red-400' : 'text-muted-foreground'
  return (
    <div className="flex items-center gap-2">
      <IconEl className={`w-3.5 h-3.5 shrink-0 ${iconCls}`} />
      <span className="text-muted-foreground">{label}</span>
      {target && (
        <code className="bg-black/30 px-1.5 py-0.5 rounded text-muted-foreground">{target}</code>
      )}
      {result?.alive ? (
        <span className={`ml-auto ${icon === 'ok' ? 'text-emerald-400' : 'text-muted-foreground'}`}>
          {result.avg_ms != null ? `${result.avg_ms.toFixed(1)} мс` : 'OK'}
          {result.loss_pct ? `, потери ${result.loss_pct}%` : ''}
        </span>
      ) : (
        // ICMP часто режется на сети/фаерволе → «недоступен» через ping не
        // означает поломку. Красным помечаем только решающие строки (icon=fail);
        // не-решающие (icon=info) — серым, чтобы не пугать ложным сбоем.
        <span className={`ml-auto ${icon === 'fail' ? 'text-red-400' : 'text-muted-foreground'}`}>недоступен</span>
      )}
    </div>
  )
}
type DiagResult = {
  vpn_endpoint_host?: string | null
  vpn_endpoint_port?: number | null
  ping_endpoint?: PingResult
  ping_direct?: PingResult
  ping_via_vpn?: PingResult
  http_via_vpn?: HttpResult
  http_direct?: HttpResult
  geo_via_vpn?: GeoInfo | null
  geo_direct?: GeoInfo | null
  sockmark_so_found?: boolean
  target_url?: string
}
import ProxyChecker from './ProxyChecker'
import ChainChecker from './ChainChecker'

type Tool = 'proxy-checker' | 'vpn-setup' | 'yandex-updates'

const TOOLS: { id: Tool; name: string; description: string; icon: typeof Shield; badge?: string }[] = [
  {
    id: 'proxy-checker',
    name: 'Прокси-чекер',
    description: 'Качество прокси (до 10 сразу) + проверка всей связки Сервер → VPN → прокси → Яндекс с вашего сервера.',
    icon: Shield,
  },
  {
    id: 'vpn-setup',
    name: 'VPN для браузера',
    description: 'Туннель только для трафика браузера (VLESS, AmneziaWG и др.). Снижает детект по разнице IP/геолокации. Система и SSH не затрагиваются.',
    icon: Globe,
    badge: 'Рекомендуется',
  },
  {
    id: 'yandex-updates',
    name: 'Апдейты Яндекса',
    description: 'Степень изменения выдачи по дням. Шторм или штиль — поможет понять, почему сегодня всё иначе.',
    icon: TrendingUp,
  },
]

const VPN_PROTO_LABELS: Record<string, string> = {
  vless: 'VLESS', vmess: 'VMess', trojan: 'Trojan',
  ss: 'Shadowsocks', hysteria2: 'Hysteria2', tuic: 'TUIC',
}

// Авто-определение протокола из вставленной ссылки/конфига (фронтовая эвристика,
// бэкенд делает финальный разбор). Возвращает null если ничего не распознано.
function detectVpnProtocol(text: string): { proto: string; label: string; security: string; server: string } | null {
  const t = (text || '').trim()
  if (!t) return null
  // AmneziaWG / WireGuard
  if (/^(vpn|awg|wg):\/\//i.test(t) || t.includes('[Interface]') ||
      (t.startsWith('{') && /interface/i.test(t))) {
    return { proto: 'amnezia', label: 'AmneziaWG', security: '', server: '' }
  }
  const m = t.match(/^([a-z0-9]+):\/\//i)
  if (!m) return null
  let scheme = m[1].toLowerCase()
  if (scheme === 'hy2') scheme = 'hysteria2'
  if (!VPN_PROTO_LABELS[scheme]) return null
  let server = '', security = ''
  try {
    const u = new URL(t.split('#')[0])
    server = u.host || ''
    const sec = (u.searchParams.get('security') || '').toLowerCase()
    if (sec === 'reality') security = 'Reality'
    else if (sec === 'tls' || sec === 'xtls') security = 'TLS'
  } catch { /* vmess (base64) и пр. — server покажет бэкенд */ }
  return { proto: scheme, label: VPN_PROTO_LABELS[scheme], security, server }
}

function VpnSetupPage({ onBack }: { onBack: () => void }) {
  const [vpnState, setVpnState] = useState<'loading' | 'not-installed' | 'installed'>('loading')
  const [installing, setInstalling] = useState(false)
  const [installLog, setInstallLog] = useState<string[]>([])
  const [status, setStatus] = useState<{ connected: boolean; name?: string; vpn_ip?: string; endpoint?: string; endpoint_host?: string; vpn_host?: string; flags?: { block_ipv6?: boolean } } | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [setupMode, setSetupMode] = useState<'key' | 'guide'>('key')
  const [keyConfig, setKeyConfig] = useState('')
  const detected = useMemo(() => detectVpnProtocol(keyConfig), [keyConfig])
  const [keyConnecting, setKeyConnecting] = useState(false)
  const [diag, setDiag] = useState<DiagResult | null>(null)
  const [diagLoading, setDiagLoading] = useState(false)
  const [diagError, setDiagError] = useState<string | null>(null)

  const runDiag = useCallback(async () => {
    setDiagLoading(true)
    setDiagError(null)
    try {
      const r = await fetch('/api/vpn/diagnose', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: 'https://yandex.ru/search' }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json()
      if (data.success === false) throw new Error(data.error || 'diagnose failed')
      setDiag(data as DiagResult)
    } catch (e: any) {
      setDiagError(e?.message || 'Ошибка диагностики')
    } finally {
      setDiagLoading(false)
    }
  }, [])

  const fetchStatus = useCallback(async () => {
    try {
      const s: any = await vpnStatus()
      if (s.installed) {
        setStatus({
          connected: s.connected,
          name: s.name,
          vpn_ip: s.vpn_ip,
          endpoint: s.endpoint,
          endpoint_host: s.endpoint_host,
          vpn_host: s.vpn_host,
          flags: s.flags,
        })
        setVpnState('installed')
      } else {
        setVpnState('not-installed')
      }
    } catch {
      setVpnState('not-installed')
    }
  }, [])

  useEffect(() => { fetchStatus() }, [fetchStatus])

  useEffect(() => {
    if (!installing) return
    const iv = setInterval(async () => {
      try {
        const r = await vpnInstallLog()
        setInstallLog(r.log)
        if (!r.installing) {
          setInstalling(false)
          clearInterval(iv)
          await fetchStatus()
        }
      } catch { /* ignore */ }
    }, 2000)
    return () => clearInterval(iv)
  }, [installing, fetchStatus])

  const handleReconnect = async () => {
    setActionLoading('reconnect')
    try { await vpnReconnect(); await fetchStatus() } catch { /* ignore */ }
    setActionLoading(null)
  }

  const handleConnectKey = async () => {
    if (!keyConfig.trim()) return
    setKeyConnecting(true)
    try {
      const r = await vpnConnectKey({ config: keyConfig.trim() })
      if (r.success) {
        await fetchStatus()
      } else {
        setInstallLog([`Ошибка: ${r.error || 'Не удалось подключить'}`])
      }
    } catch {
      setInstallLog(['Ошибка подключения по ключу'])
    }
    setKeyConnecting(false)
  }

  const handleToggleIpv6Block = async (enabled: boolean) => {
    setActionLoading('flag_ipv6')
    try {
      await vpnSetFlag('block_ipv6', enabled)
      await fetchStatus()
    } catch { /* ignore */ }
    setActionLoading(null)
  }

  const handleDisable = async () => {
    setActionLoading('disable')
    try {
      await vpnDisable()
      await fetchStatus()
      setDiag(null)
    } catch { /* ignore */ }
    setActionLoading(null)
  }

  const handleRemove = async () => {
    if (!window.confirm('Удалить VPN полностью? Конфигурация будет стёрта. Для временного отключения используйте «Отключить».')) {
      return
    }
    setActionLoading('remove')
    try {
      await vpnRemove()
      setStatus(null)
      setVpnState('not-installed')
      setKeyConfig('')
      setInstallLog([])
      setDiag(null)
    } catch { /* ignore */ }
    setActionLoading(null)
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-border">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-lg font-bold">VPN для браузера</h1>
          <p className="text-xs text-muted-foreground">Туннель оборачивает только трафик браузера — сервер, SSH и панель не затрагиваются</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 max-w-3xl">

        {vpnState === 'loading' && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {vpnState === 'installed' && status && (
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status.connected
                  ? <Wifi className="w-5 h-5 text-emerald-400" />
                  : <WifiOff className="w-5 h-5 text-red-400" />}
                <div>
                  <p className="text-sm font-semibold">
                    {status.connected ? 'VPN подключён' : 'VPN отключён'}
                    {status.name && (
                      <span className="ml-2 text-muted-foreground font-normal">— {status.name}</span>
                    )}
                  </p>
                  {status.endpoint && (
                    <p className="text-xs text-muted-foreground mt-0.5">Endpoint: {status.endpoint}</p>
                  )}
                </div>
              </div>
              <Badge variant={status.connected ? 'default' : 'destructive'} className={status.connected ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/15' : ''}>
                {status.connected ? 'Активен' : 'Неактивен'}
              </Badge>
            </div>

            <div className="flex items-center gap-x-6 gap-y-1.5 text-xs flex-wrap">
              {status.endpoint_host && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground" title="Внешний IP VPN-сервера — его видят сайты, когда трафик идёт через VPN">
                    Внешний IP VPN:
                  </span>
                  <code className="bg-black/30 px-2 py-0.5 rounded text-emerald-400 font-mono">{status.endpoint_host}</code>
                </div>
              )}
              {status.vpn_ip && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground" title="Внутренний адрес клиента в VPN-туннеле (служебный)">
                    Адрес в туннеле:
                  </span>
                  <code className="bg-black/30 px-2 py-0.5 rounded text-muted-foreground font-mono">{status.vpn_ip}</code>
                </div>
              )}
            </div>

            {/* IPv6 kill-switch toggle */}
            {status.connected && (
              <div className="rounded-lg border border-border bg-muted/20 p-2.5 mt-2">
                <div className="flex items-start gap-3">
                  <button
                    role="switch"
                    aria-checked={!!status.flags?.block_ipv6}
                    disabled={actionLoading !== null}
                    onClick={() => handleToggleIpv6Block(!status.flags?.block_ipv6)}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-md border transition-colors ${
                      status.flags?.block_ipv6
                        ? 'border-emerald-500/30 bg-emerald-500/20'
                        : 'border-border bg-muted'
                    } ${actionLoading === 'flag_ipv6' ? 'opacity-50' : ''}`}
                  >
                    <span className={`absolute left-0.5 h-4 w-4 rounded-[calc(var(--radius)-3px)] transition-transform ${
                      status.flags?.block_ipv6 ? 'translate-x-[17px] bg-emerald-400' : 'translate-x-0 bg-muted-foreground'
                    }`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold flex items-center gap-2">
                      Блокировать IPv6 (kill-switch)
                      {status.flags?.block_ipv6
                        ? <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-emerald-500/30 text-emerald-400">ON</Badge>
                        : <Badge variant="outline" className="h-4 px-1.5 text-[10px] border-amber-500/30 text-amber-400">OFF</Badge>
                      }
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                      {status.flags?.block_ipv6 ? (
                        <>
                          Строгий режим: весь IPv6-трафик блокируется. Сайты видят только IP VPN (защита от корреляции v4 МСК + v6 Hetzner).
                          <span className="text-amber-400"> Если используете IPv6-прокси — выключите.</span>
                        </>
                      ) : (
                        <>
                          IPv6 работает напрямую через сервер. Нужно для IPv6-прокси.
                          <span className="text-red-400"> Внимание: при работе без прокси сайты увидят v6 адрес сервера (Hetzner).</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1 flex-wrap">
              {status.connected && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs gap-1.5"
                  disabled={actionLoading !== null || diagLoading}
                  onClick={runDiag}
                >
                  {diagLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
                  Проверить соединение
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5"
                disabled={actionLoading !== null}
                onClick={handleReconnect}
                title={status.connected ? 'Переподключить туннель' : 'Включить VPN с сохранённым конфигом'}
              >
                {actionLoading === 'reconnect' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {status.connected ? 'Переподключить' : 'Включить'}
              </Button>
              {status.connected && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs gap-1.5"
                  disabled={actionLoading !== null}
                  onClick={handleDisable}
                  title="Опустить интерфейс и убрать маршрутизацию. Конфиг остаётся — можно включить обратно одной кнопкой."
                >
                  {actionLoading === 'disable' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <WifiOff className="w-3.5 h-3.5" />}
                  Отключить
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5 text-red-400 hover:text-red-300 border-red-500/20 hover:border-red-500/40 hover:bg-red-500/10"
                disabled={actionLoading !== null}
                onClick={handleRemove}
                title="Удалить VPN полностью (стирает конфиг). Для смены VPN — удалите и подключите новый ключ."
              >
                {actionLoading === 'remove' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Удалить VPN
              </Button>
            </div>

            {(diag || diagError || diagLoading) && (
              <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">Диагностика соединения</span>
                  {!diagLoading && (
                    <button
                      onClick={() => { setDiag(null); setDiagError(null) }}
                      className="text-muted-foreground hover:text-foreground"
                    ><X className="w-3.5 h-3.5" /></button>
                  )}
                </div>

                {diagLoading && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Пинг до VPN + тест запроса на yandex.ru…
                  </div>
                )}

                {diagError && (
                  <div className="text-red-400">Ошибка: {diagError}</div>
                )}

                {diag && !diagLoading && (
                  <>
                    {/* 1) Ping → VPN endpoint (НЕ решающий: ICMP часто режется,
                        итоговый вердикт — на HTTP через VPN ниже) */}
                    <PingRow
                      icon={diag.ping_endpoint?.alive ? 'ok' : 'info'}
                      label="Пинг до VPN сервера"
                      target={
                        (diag.vpn_endpoint_host || '—') +
                        (diag.vpn_endpoint_port ? `:${diag.vpn_endpoint_port}` : '')
                      }
                      result={diag.ping_endpoint}
                    />

                    {/* 2) Ping → yandex.ru DIRECT */}
                    <PingRow
                      icon={diag.ping_direct?.alive ? 'info' : 'fail'}
                      label="Пинг до yandex.ru"
                      target="напрямую"
                      result={diag.ping_direct}
                    />

                    {/* 3) Ping → yandex.ru VIA VPN */}
                    <PingRow
                      icon={diag.ping_via_vpn?.alive ? 'ok' : 'fail'}
                      label="Пинг до yandex.ru"
                      target="через VPN"
                      result={diag.ping_via_vpn}
                    />

                    {/* HTTP status summary */}
                    <div className="pt-2 mt-2 border-t border-border/50">
                      <div className="flex items-center gap-2">
                        {diag.http_via_vpn?.ok
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          : <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                        <span className="text-muted-foreground">HTTP-запрос yandex.ru через VPN</span>
                        {diag.http_via_vpn?.ok ? (
                          <span className="text-emerald-400 ml-auto">
                            HTTP {diag.http_via_vpn.http_code}
                            {diag.http_via_vpn.time_total_ms ? `, ${diag.http_via_vpn.time_total_ms} мс` : ''}
                          </span>
                        ) : (
                          <span className="text-red-400 ml-auto truncate max-w-[50%]">
                            {diag.http_via_vpn?.error || `HTTP ${diag.http_via_vpn?.http_code || 'fail'}`}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Geolocation cards */}
                    {(diag.geo_via_vpn || diag.geo_direct) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                        <GeoCard title="Выход через VPN" geo={diag.geo_via_vpn} highlight />
                        <GeoCard title="Реальный IP сервера" geo={diag.geo_direct} />
                      </div>
                    )}

                    {diag.http_via_vpn?.ip_seen && diag.http_direct?.ip_seen &&
                     diag.http_via_vpn.ip_seen === diag.http_direct.ip_seen && (
                      <div className="mt-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-amber-300">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                        IP совпадают — трафик через VPN НЕ идёт. Проверьте правила маршрутизации.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {(vpnState === 'not-installed' || (vpnState === 'installed' && !!status && !status.connected)) && (
          <div className="space-y-4">
            {vpnState === 'installed' && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="flex-1 h-px bg-border" />
                <span>или подключить другой VPN</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            )}
            <div className="inline-flex w-full rounded-lg bg-muted/60 p-1 md:p-1.5 gap-1">
              <button
                onClick={() => setSetupMode('key')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${setupMode === 'key' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Готовый ключ / ссылка
              </button>
              <button
                onClick={() => setSetupMode('guide')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${setupMode === 'guide' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Как поднять свой VPN
              </button>
            </div>

            {setupMode === 'guide' && (
              <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <div>
                  <p className="text-sm font-semibold mb-1 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-emerald-400" /> Как поднять свой VPN-сервер
                  </p>
                  <p className="text-xs text-muted-foreground">Поднимите узел в нужном регионе и получите ссылку — затем вставьте её на вкладке «Готовый ключ / ссылка».</p>
                </div>

                <ol className="space-y-3 text-xs text-muted-foreground">
                  <li className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-md bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">1</span>
                    <div>
                      <span className="text-foreground font-medium">Возьмите VPS</span> в нужной стране (подойдёт самый дешёвый, 1 vCPU / 1 ГБ). Лучше там же, где ваши прокси — чтобы регион совпадал.
                    </div>
                  </li>
                  <li className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-md bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">2</span>
                    <div>
                      Установите панель <span className="text-foreground font-medium">3X-UI</span> (или Marzban) — это веб-интерфейс для VLESS/VMess/Trojan. Ставится одной командой:
                      <code className="block mt-1.5 bg-black/30 px-2 py-1 rounded font-mono text-[11px] text-emerald-300 break-all">bash &lt;(curl -Ls https://raw.githubusercontent.com/mhsanaei/3x-ui/master/install.sh)</code>
                    </div>
                  </li>
                  <li className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-md bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">3</span>
                    <div>
                      В панели создайте <span className="text-foreground font-medium">VLESS + Reality</span> (рекомендуется — лучше всего обходит блокировки). SNI поставьте на «живой» сайт, например <code className="bg-black/30 px-1 py-0.5 rounded font-mono text-[11px]">www.nvidia.com</code>.
                    </div>
                  </li>
                  <li className="flex gap-2.5">
                    <span className="shrink-0 w-5 h-5 rounded-md bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">4</span>
                    <div>
                      Скопируйте ссылку клиента (<code className="bg-black/30 px-1 py-0.5 rounded font-mono text-[11px]">vless://…</code>) и вставьте на вкладке <span className="text-foreground font-medium">«Готовый ключ / ссылка»</span>. Поддерживаются также vmess, trojan, shadowsocks, hysteria2, tuic и ключи AmneziaWG.
                    </div>
                  </li>
                </ol>

                <div className="flex items-start gap-2.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                  <Info className="w-4 h-4 shrink-0 text-emerald-400 mt-0.5" />
                  <p className="text-[11px] text-emerald-200/80 leading-relaxed">
                    Скоро мы сделаем так, что ассистент сможет <span className="font-medium text-emerald-300">поднимать VPN автоматически</span> — достаточно будет указать данные сервера. А пока — разворачивайте сами по инструкции выше.
                  </p>
                </div>
              </div>
            )}

            {setupMode === 'key' && (
              <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold mb-1">Способ подключения</p>
                    <p className="text-xs text-muted-foreground">Вставьте ссылку подписки или ключ — протокол определится автоматически</p>
                  </div>
                  {detected && (
                    <Badge variant="outline" className="shrink-0 border-emerald-500/30 text-emerald-400 bg-emerald-500/10 gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {detected.label}{detected.security ? ` + ${detected.security}` : ''}
                    </Badge>
                  )}
                </div>
                <div>
                  <textarea
                    value={keyConfig}
                    onChange={e => setKeyConfig(e.target.value)}
                    disabled={keyConnecting}
                    placeholder={"vless://… · vmess://… · trojan://… · ss://… · hysteria2://… · tuic://…\n\nили ключ Amnezia vpn://… / WG-конфиг [Interface]…"}
                    className="w-full h-32 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                {detected && (detected.server || detected.security) && (
                  <div className="flex items-center gap-x-5 gap-y-1.5 text-xs flex-wrap rounded-lg bg-muted/30 border border-border px-3 py-2">
                    <span className="flex items-center gap-1.5">
                      <span className="text-muted-foreground">Протокол:</span>
                      <span className="text-foreground font-medium">{detected.label}</span>
                    </span>
                    {detected.server && (
                      <span className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Сервер:</span>
                        <code className="bg-black/30 px-1.5 py-0.5 rounded text-emerald-400 font-mono">{detected.server}</code>
                      </span>
                    )}
                    {detected.security && (
                      <span className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Шифрование:</span>
                        <span className="text-foreground font-medium">{detected.security}</span>
                      </span>
                    )}
                  </div>
                )}
                {keyConfig.trim() && !detected && (
                  <p className="text-xs text-amber-400 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Не похоже на поддерживаемую ссылку или конфиг. Проверьте формат.
                  </p>
                )}
                <Button size="sm" className="h-9 gap-2 w-full" disabled={keyConnecting || !keyConfig.trim() || !detected} onClick={handleConnectKey}>
                  {keyConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                  {keyConnecting ? 'Подключение…' : 'Подключить'}
                </Button>
              </div>
            )}

            {installLog.length > 0 && (
              <div className="bg-black/40 rounded-lg border border-white/5 p-3 max-h-60 overflow-y-auto">
                <div className="space-y-0.5 font-mono text-[11px]">
                  {installLog.map((line, i) => (
                    <p key={i} className={line.startsWith('❌') || line.startsWith('Ошибка') ? 'text-red-400' : line.startsWith('✅') ? 'text-emerald-400' : 'text-muted-foreground'}>{line}</p>
                  ))}
                  {installing && (
                    <p className="text-muted-foreground/50 flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" /> Выполняется...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-500 mt-0.5" />
          <div className="text-xs text-amber-800 dark:text-amber-200/80 leading-relaxed space-y-1.5">
            <p className="font-medium text-amber-400">Когда и зачем включать VPN?</p>
            <p>
              Часть российских прокси-провайдеров (особенно мобильные и резидентские) пропускают запросы только
              от клиентов внутри РФ — с европейского датацентра подключиться невозможно. VPN с сервером
              в нужном регионе снимает этот барьер: для прокси вы выглядите как обычный локальный клиент.
            </p>
            <p>
              Параллельно VPN закрывает целый класс тонких утечек, которые прокси в одиночку не решает:
              нативный IPv6-адрес сервера, WebRTC/STUN, DNS-запросы в обход прокси, fallback-трафик
              Chromium'а при обрыве SOCKS. Это не «невидимка на 100%», но планку для антифрода
              заметно поднимает — и превращает связку «прокси + сервер» в выстроенный туннель,
              а не в набор разношёрстных источников.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

type YandexDay = { date: string; top10_change: number; top50_change: number }
type YandexResp = { days: YandexDay[]; custom_updates: Record<string, string>; last_fetch_ts: number; source: string }

// Soft palette — traffic-light, calm on the eyes.
const CLR_CALM = '#22c55e'       // emerald-500
const CLR_ELEVATED = '#eab308'   // amber-500
const CLR_STORM = '#ef4444'      // red-500
const CLR_CALM_RGBA = 'rgba(34, 197, 94, 0.75)'
const CLR_ELEVATED_RGBA = 'rgba(234, 179, 8, 0.80)'
const CLR_STORM_RGBA = 'rgba(239, 68, 68, 0.80)'

// Thresholds: calm <15, elevated 15-20, storm >=20
const LVL_CALM_MAX = 15
const LVL_ELEVATED_MAX = 20
const levelOf = (v: number): 'calm' | 'elevated' | 'storm' =>
  v >= LVL_ELEVATED_MAX ? 'storm' : v >= LVL_CALM_MAX ? 'elevated' : 'calm'
const levelName = (lvl: 'calm' | 'elevated' | 'storm') =>
  lvl === 'storm' ? 'Шторм' : lvl === 'elevated' ? 'Колебания' : 'Спокойно'
const levelColor = (lvl: 'calm' | 'elevated' | 'storm') =>
  lvl === 'storm' ? CLR_STORM : lvl === 'elevated' ? CLR_ELEVATED : CLR_CALM
const levelColorSoft = (lvl: 'calm' | 'elevated' | 'storm') =>
  lvl === 'storm' ? CLR_STORM_RGBA : lvl === 'elevated' ? CLR_ELEVATED_RGBA : CLR_CALM_RGBA

function YandexTooltip({ active, payload, label, notes }: any) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload as YandexDay
  const [y, m, day] = (label as string).split('-')
  const note = notes?.[String(parseInt(day, 10))]
  const lvl = levelOf(d.top10_change)
  const lvlColor = levelColor(lvl)
  return (
    <div className="rounded-lg border shadow-lg px-3 py-2 text-xs min-w-[180px]"
      style={{ background: 'var(--popover)', borderColor: 'var(--border)', color: 'var(--popover-foreground)' }}>
      <div className="font-medium mb-1">{day}.{m}.{y}</div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">ТОП-10</span>
        <span className="font-semibold tabular-nums" style={{ color: lvlColor }}>{d.top10_change.toFixed(1)}%</span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">ТОП-50</span>
        <span className="font-medium tabular-nums">{d.top50_change.toFixed(1)}%</span>
      </div>
      <div className="flex items-center justify-between gap-3 mt-1 pt-1 border-t" style={{ borderColor: 'var(--border)' }}>
        <span className="text-muted-foreground">Уровень</span>
        <span className="font-medium" style={{ color: lvlColor }}>{levelName(lvl)}</span>
      </div>
      {note && (
        <div className="mt-1.5 pt-1.5 border-t text-[11px] leading-snug" style={{ borderColor: 'var(--border)' }}>
          <span className="text-muted-foreground">Событие: </span>
          <span className="text-foreground">{note}</span>
        </div>
      )}
    </div>
  )
}

function YandexUpdatesPage({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<YandexResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [days, setDays] = useState(30)

  const load = useCallback(async () => {
    setLoading(true); setErr(null)
    try {
      const r = await fetch(`/api/yandex-updates?days=${days}`)
      const j = await r.json()
      if (!r.ok || j?.error) throw new Error(j?.error || `HTTP ${r.status}`)
      setData(j)
    } catch (e: any) {
      setErr(e?.message || 'Не удалось загрузить данные')
    }
    setLoading(false)
  }, [days])

  useEffect(() => { void load() }, [load])

  const today = data?.days?.[data.days.length - 1]
  const yesterday = data?.days?.[data.days.length - 2]
  const avg30 = useMemo(() => {
    if (!data?.days?.length) return 0
    return data.days.reduce((s, d) => s + d.top10_change, 0) / data.days.length
  }, [data])
  const fmtDateTick = (s: string) => {
    const [, m, d] = s.split('-')
    return `${d}.${m}`
  }
  const lastFetchAgo = data?.last_fetch_ts ? Math.floor((Date.now() / 1000 - data.last_fetch_ts) / 60) : null

  // Custom_updates is keyed by day-of-month (of the current month shown by Pixel Tools).
  // Attach only to days within the fetched range.
  const eventsList = useMemo(() => {
    if (!data?.days?.length || !data.custom_updates) return []
    const out: { date: string; text: string }[] = []
    for (const d of data.days) {
      const day = parseInt(d.date.split('-')[2], 10)
      const note = data.custom_updates[String(day)]
      if (note) out.push({ date: d.date, text: note })
    }
    return out
  }, [data])
  const eventDates = useMemo(() => new Set(eventsList.map(e => e.date)), [eventsList])

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-border">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Апдейты Яндекса
          </h1>
          <p className="text-xs text-muted-foreground">Степень изменения SERP по дням · источник: Pixel Tools</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 max-w-4xl space-y-4">
        {/* Today snapshot — compact row */}
        {today && (
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-border bg-card px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Сегодня</div>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <div className="text-xl font-semibold tabular-nums" style={{ color: levelColor(levelOf(today.top10_change)) }}>
                  {today.top10_change.toFixed(1)}<span className="text-sm opacity-70">%</span>
                </div>
                <span className="text-[10px] font-medium" style={{ color: levelColor(levelOf(today.top10_change)) }}>
                  {levelName(levelOf(today.top10_change))}
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Вчера</div>
              <div className="text-xl font-semibold tabular-nums text-foreground mt-0.5">
                {yesterday ? yesterday.top10_change.toFixed(1) : '—'}
                <span className="text-sm opacity-70">%</span>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card px-3 py-2.5">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Среднее / {days} дн.</div>
              <div className="text-xl font-semibold tabular-nums text-foreground mt-0.5">
                {avg30.toFixed(1)}<span className="text-sm opacity-70">%</span>
              </div>
            </div>
          </div>
        )}

        {/* Range selector */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {[14, 30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`h-7 px-2.5 text-[11px] rounded-md transition-colors ${
                days === d
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {d} дн.
            </button>
          ))}
          {lastFetchAgo !== null && (
            <span className="text-[10px] text-muted-foreground/70 ml-auto">
              обновлено {lastFetchAgo < 60 ? `${lastFetchAgo} мин` : `${Math.floor(lastFetchAgo / 60)} ч`} назад
            </span>
          )}
        </div>

        {/* Chart */}
        <div className="rounded-lg border border-border bg-card p-3 md:p-4">
          {loading && !data ? (
            <div className="flex items-center justify-center h-[220px] text-muted-foreground gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Загрузка данных…
            </div>
          ) : err ? (
            <div className="flex items-center justify-center h-[220px] text-destructive text-sm">{err}</div>
          ) : !data?.days?.length ? (
            <div className="flex items-center justify-center h-[220px] text-muted-foreground text-sm">Нет данных</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.days} margin={{ top: 24, right: 8, left: -12, bottom: 0 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} opacity={0.5} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDateTick}
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis
                  unit="%"
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                  width={38}
                />
                <Tooltip
                  cursor={{ fill: 'var(--accent)', opacity: 0.25 }}
                  content={<YandexTooltip notes={data.custom_updates} />}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Bar
                  dataKey="top10_change"
                  name="ТОП-10"
                  radius={[2, 2, 0, 0]}
                  shape={(p: any) => {
                    const { x, y, width, height, payload } = p
                    const lvl = levelOf(payload.top10_change)
                    const fill = levelColorSoft(lvl)
                    const solid = levelColor(lvl)
                    const hasEvent = eventDates.has(payload.date)
                    const cx = x + width / 2
                    return (
                      <g>
                        <rect x={x} y={y} width={width} height={height} rx={2} ry={2} fill={fill} />
                        {hasEvent && (
                          <>
                            {/* dashed line from bar top to marker */}
                            <line x1={cx} y1={y - 4} x2={cx} y2={y - 14} stroke={solid} strokeWidth={1} strokeDasharray="2 2" opacity={0.7} />
                            {/* outer glow */}
                            <circle cx={cx} cy={y - 17} r={5.5} fill={solid} opacity={0.25} />
                            {/* marker dot */}
                            <circle cx={cx} cy={y - 17} r={3.5} fill={solid} stroke="var(--card)" strokeWidth={1.5} />
                          </>
                        )}
                      </g>
                    )
                  }}
                />
                {/* extra top padding is handled via margin.top=20 below */}
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* Legend — inside chart card, small */}
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap mt-2 pt-2 border-t border-border/60">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: CLR_CALM_RGBA }} /> Спокойно &lt; 15%</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: CLR_ELEVATED_RGBA }} /> Колебания 15–20%</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: CLR_STORM_RGBA }} /> Шторм ≥ 20%</span>
            {eventsList.length > 0 && (
              <span className="flex items-center gap-1.5 ml-auto">
                <span className="relative inline-block w-2.5 h-2.5">
                  <span className="absolute inset-0 rounded-full bg-foreground/80" />
                </span>
                событие
              </span>
            )}
          </div>
        </div>

        {/* Custom updates / events */}
        {eventsList.length > 0 && (
          <div className="rounded-lg border border-border bg-card p-3 md:p-4">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
              <Info className="w-3 h-3" />
              События и изменения
            </div>
            <ul className="space-y-1.5">
              {eventsList.slice().reverse().map(ev => {
                const [y, m, day] = ev.date.split('-')
                return (
                  <li key={ev.date} className="flex gap-3 text-[12px] leading-snug">
                    <span className="text-muted-foreground tabular-nums shrink-0 min-w-[52px]">{day}.{m}.{y}</span>
                    <span className="text-foreground">{ev.text}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        <div className="text-[11px] text-muted-foreground/70 leading-relaxed">
          В «штормовые» дни Яндекс заметно меняет выдачу — могут резко вырасти позиции, увеличиться число
          капч и показателя 429 у проектов. Эти данные помогают понять, связаны ли всплески с обновлением
          алгоритмов или с локальными проблемами.
        </div>
      </div>
    </div>
  )
}

interface ToolsPageProps {
  selectedTool?: string | null
  onSelectChange?: (slug: string | null) => void
}

export default function ToolsPage({ selectedTool: controlledTool, onSelectChange }: ToolsPageProps = {}) {
  const [internalTool, setInternalTool] = useState<Tool | null>(null)
  const [checkerMode, setCheckerMode] = useState<'bulk' | 'chain'>('bulk')
  const activeTool = (controlledTool !== undefined ? (controlledTool as Tool | null) : internalTool)
  const setActiveTool = (t: Tool | null) => {
    if (onSelectChange) onSelectChange(t)
    else setInternalTool(t)
  }

  if (activeTool === 'yandex-updates') {
    return <YandexUpdatesPage onBack={() => setActiveTool(null)} />
  }

  if (activeTool === 'proxy-checker') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-border">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setActiveTool(null)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Прокси-чекер</h1>
            <p className="text-xs text-muted-foreground">
              {checkerMode === 'bulk'
                ? 'Проверка работоспособности и качества прокси'
                : 'Полная связка Сервер → VPN → прокси → Яндекс — с вашего сервера'}
            </p>
          </div>
          <div className="inline-flex rounded-lg bg-muted/60 p-1 gap-1">
            <button
              onClick={() => setCheckerMode('bulk')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${checkerMode === 'bulk' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Массовая проверка
            </button>
            <button
              onClick={() => setCheckerMode('chain')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${checkerMode === 'chain' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Проверка связки
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {checkerMode === 'bulk' ? <ProxyChecker /> : <ChainChecker />}
        </div>
      </div>
    )
  }

  if (activeTool === 'vpn-setup') {
    return <VpnSetupPage onBack={() => setActiveTool(null)} />
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
          <Wrench className="w-5 h-5 text-primary" />
          Инструменты и сервисы
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Утилиты для работы с прокси, профилями и данными</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TOOLS.map(tool => {
          const Icon = tool.icon
          return (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              className="group flex flex-col gap-3 p-5 rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-accent/50 transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <span className="font-semibold text-sm">{tool.name}</span>
                {tool.badge && (
                  <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                    {tool.badge}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{tool.description}</p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
